import type { ReactNode } from 'react';
import { Info, Mail, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AUTH_EMAIL_SPAM_FOLDER_HINT } from '@/lib/auth-email-copy';
export type AuthEmailSentIntent = 'sign-in' | 'sign-up';
/** REK-1986: the panel also fronts the NEW SMS OTP flow (phone login tab). */
export type AuthSentChannel = 'email' | 'sms';

const ACTION_LINES: Record<AuthEmailSentIntent, string> = {
  'sign-in': 'Open the link in your email to sign in.',
  'sign-up': 'Open the link in your email to finish signing up.',
};

// REK-1986 (SSO.5, D-2): under Clerk the auth email carries a 6-digit code
// only — no magic link — so the action line must not mention a link.
const CODE_ACTION_LINES: Record<AuthEmailSentIntent, string> = {
  'sign-in': 'Enter the 6-digit code from your email to sign in.',
  'sign-up': 'Enter the 6-digit code from your email to finish signing up.',
};

const SMS_ACTION_LINES: Record<AuthEmailSentIntent, string> = {
  'sign-in': 'Enter the 6-digit code we texted you to sign in.',
  'sign-up': 'Enter the 6-digit code we texted you to finish signing up.',
};

const SMS_DELIVERY_HINT =
  'Texts usually arrive within a few seconds, but can occasionally take a minute.';

/** Ghost button styling for secondary actions (e.g. use a different email). */
export const AUTH_EMAIL_SECONDARY_ACTION_CLASS =
  'w-full text-sm text-muted-foreground hover:text-foreground';

export interface AuthEmailSentPanelProps {
  /** The address the code/link went to — an email, or an E.164/formatted phone for `channel="sms"`. */
  email: string;
  intent: AuthEmailSentIntent;
  /** Delivery channel — adjusts copy only, structure is identical. Defaults to email. */
  channel?: AuthSentChannel;
  resendCooldown: number;
  isLoading: boolean;
  onResend: () => void;
  error?: string | null;
  /** OTP form or other content shown below the card (code-entry mode) */
  children?: ReactNode;
  /** Inline underlined link after the action line (hidden while `children` is shown) */
  onEnterCodeInstead?: () => void;
  /** Optional link below the spam/resend block (e.g. use a different email) */
  trailingAction?: ReactNode;
  showIcon?: boolean;
  /**
   * REK-1930: force the resend control disabled regardless of `resendCooldown`
   * (e.g. while a rate-limit cooldown is active and the countdown is shown in
   * the surrounding copy rather than on the button).
   */
  disableResend?: boolean;
}

export function AuthEmailSentPanel({
  email,
  intent,
  channel = 'email',
  resendCooldown,
  isLoading,
  onResend,
  error,
  children,
  onEnterCodeInstead,
  trailingAction,
  showIcon = true,
  disableResend = false,
}: AuthEmailSentPanelProps) {
  const showCodeLink = onEnterCodeInstead && !children;
  const resendDisabled = resendCooldown > 0 || isLoading || disableResend;
  const isSms = channel === 'sms';
  // D-2: Clerk auth emails are OTP-code-only (no magic link).
  const actionLine = isSms ? SMS_ACTION_LINES[intent] : CODE_ACTION_LINES[intent];
  const resendLabel = isSms ? 'Resend code' : 'Resend email';
  const SentIcon = isSms ? Smartphone : Mail;

  return (
    <div className="w-full space-y-4 text-center">
      {showIcon && (
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
          <SentIcon className="h-7 w-7 text-primary" aria-hidden />
        </div>
      )}

      <div className="rounded-xl border border-border/60 bg-muted/40 px-4 py-4 text-left sm:text-center">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sent to</p>
        <p className="mt-1 break-all text-base font-medium text-foreground">{email}</p>
        <p className="mt-2 text-sm text-muted-foreground">
          {actionLine}
          {showCodeLink && (
            <>
              {' '}
              <Button
                type="button"
                variant="link"
                onClick={onEnterCodeInstead}
                disabled={isLoading}
                className="h-auto p-0 text-sm font-medium text-foreground underline underline-offset-2 hover:text-primary"
              >
                Enter 6-digit code instead
              </Button>
            </>
          )}
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-border/40 bg-muted/30 px-3 py-3 text-left text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden />
        <p>
          {isSms ? SMS_DELIVERY_HINT : AUTH_EMAIL_SPAM_FOLDER_HINT} Still can&apos;t find it?{' '}
          <Button
            type="button"
            variant="link"
            onClick={onResend}
            disabled={resendDisabled}
            aria-label={
              resendCooldown > 0
                ? `Resend in ${resendCooldown} seconds`
                : disableResend
                  ? 'Resend temporarily unavailable due to too many requests'
                  : resendLabel
            }
            className={
              resendDisabled
                ? 'h-auto cursor-default p-0 text-xs font-medium text-primary no-underline hover:text-primary disabled:opacity-100'
                : 'h-auto p-0 text-xs font-medium text-primary underline underline-offset-2 hover:text-primary/90'
            }
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : resendLabel}
          </Button>
        </p>
      </div>

      {children}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {trailingAction ? <div className="w-full">{trailingAction}</div> : null}
    </div>
  );
}
